/**
 * GoogleAuthProvider — bootstraps Google Identity Services for the SPA.
 *
 * GMAIL-ONLY-AUTH (2026-02-27)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Pulls the operator-configured Client ID from
 * `GET /api/public/auth/google-config` at startup and wraps the entire
 * app in <GoogleOAuthProvider>. If the operator has not configured
 * Google yet, the provider still mounts (with `enabled: false`) so
 * downstream components can render a friendly "Google sign-in is not
 * yet enabled — contact your administrator" empty state instead of
 * crashing.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { api } from "@/lib/api";

const Ctx = createContext({ enabled: false, clientId: null, loading: true });

export const useGoogleAuthConfig = () => useContext(Ctx);

export default function GoogleAuthProvider({ children }) {
  const [state, setState] = useState({ enabled: false, clientId: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    api.get("/public/auth/google-config")
      .then((r) => {
        if (cancelled) return;
        const d = r?.data?.data || {};
        setState({ enabled: !!d.enabled, clientId: d.client_id || null, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ enabled: false, clientId: null, loading: false });
      });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => state, [state]);

  // When no client_id is configured we still render the provider with a
  // placeholder so React doesn't unmount the tree. Components must guard
  // against `enabled === false` themselves and avoid calling Google APIs.
  const effectiveClientId = state.clientId || "GOOGLE_AUTH_NOT_CONFIGURED.apps.googleusercontent.com";

  return (
    <Ctx.Provider value={value}>
      <GoogleOAuthProvider clientId={effectiveClientId}>
        {children}
      </GoogleOAuthProvider>
    </Ctx.Provider>
  );
}
